import React, { useContext, useEffect, useState } from 'react'
import { Button, Icon, TYPE_OUTLINE } from '@mds/mds-reactjs-library'
import Outline24CInfo from '@mds/mds-icons/icons/svg/outline-24-c-info.svg'
import { ChargeCodeContext } from '../../../Contexts/ChargeCode/ChargeCodeContext'
import { useCreateToast } from '../../../Hooks/useCreateToast'
import ReportingEndpoints from '../../../Services/ReportingEndpoints'
import { getCurrentUserEmail } from '../../../Utility/TokenUtility'
import { buildChargeCodeDetailsJson } from '../../../Utility/ChargeCodeDetailsPayload'
import { ValidationErrorMessage } from './chargeCodeValidationHelpers'
import * as S from './ChargeCodeModalContent.styles'

export function ChargeCodeModalContent() {
  const { chargeCode, setChargeCode, chargeCodeModal, closeChargeCodeModal } = useContext(ChargeCodeContext)
  const { createSuccessToast, createWarningToast } = useCreateToast()
  const [loading, setLoading] = useState(false)
  const [pricingExpanded, setPricingExpanded] = useState(false)
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false)
  const [edEmail, setEdEmail] = useState('')
  const [isEd, setIsEd] = useState(false)
  const [agreePricingPolicy, setAgreePricingPolicy] = useState(false)
  const [warRoomChargeCode, setWarRoomChargeCode] = useState(false)

  useEffect(() => {
    if (chargeCode?.ed_email) {
      setEdEmail(chargeCode.ed_email)
    }
    if (chargeCode?.isIAmEDChecked != null) {
      setIsEd(!!chargeCode.isIAmEDChecked)
    }
    if (chargeCode?.isAgreementPolicyChecked != null) {
      setAgreePricingPolicy(!!chargeCode.isAgreementPolicyChecked)
    }
    if (chargeCode?.isWarRoomChecked != null) {
      setWarRoomChargeCode(!!chargeCode.isWarRoomChecked)
    }
    // Prefill once when modal opens (content remounts each time)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { pendingAction, actionLabel, datasetId } = chargeCodeModal
  const hasChargeCode = chargeCode.value && chargeCode.value.length >= 8
  const hasEdInfo = edEmail.trim() !== '' || isEd
  const hasAgreementPolicy = agreePricingPolicy
  const isButtonDisabled = !hasChargeCode || !hasEdInfo || !hasAgreementPolicy
  const isEnabled = chargeCode.value.length < 8 && chargeCode.value !== ''
  const buttonTitle = actionLabel ? `Submit & ${actionLabel}` : 'Submit'

  const getDetailsForChargeCode = () => {
    const edEmailForPayload = isEd ? getCurrentUserEmail() : edEmail.trim()
    const edEmailNormalized = edEmailForPayload ? edEmailForPayload.toLowerCase() : ''
    const detailsStr = buildChargeCodeDetailsJson({
      chargeCodeValue: chargeCode.value,
      eDEmail: edEmailNormalized,
      isIAmEDChecked: isEd,
      isAgreementPolicyChecked: agreePricingPolicy,
      isWarRoomChecked: warRoomChargeCode
    })
    return {
      detailsStr,
      edEmailNormalized,
      isIAmEDChecked: isEd,
      isAgreementPolicyChecked: agreePricingPolicy,
      isWarRoomChecked: warRoomChargeCode
    }
  }

  const finishSuccessfulChargeCode = (data, detailBundle) => {
    const {
      detailsStr,
      edEmailNormalized,
      isIAmEDChecked,
      isAgreementPolicyChecked,
      isWarRoomChecked
    } = detailBundle
    setChargeCode({
      value: chargeCode.value,
      isActive: data.isActive,
      isChargeable: data.isChargeable,
      isClient: data.isClient,
      ed_email: edEmailNormalized,
      isIAmEDChecked,
      isAgreementPolicyChecked,
      isWarRoomChecked
    })
    createSuccessToast('Charge code successfully validated')
    closeChargeCodeModal()
    if (typeof pendingAction === 'function') {
      const extra = detailsStr ? { details: detailsStr } : {}
      setTimeout(() => pendingAction(extra), 250)
    }
  }

  const validateAndSubmit = () => {
    if (isButtonDisabled) return
    setLoading(true)
    ReportingEndpoints.validateChargeCode(chargeCode.value)
      .then(({ data }) => {
        const isValid = data.isActive && data.isChargeable && data.isClient
        if (!isValid) {
          setChargeCode({
            value: chargeCode.value,
            isActive: data.isActive,
            isChargeable: data.isChargeable,
            isClient: data.isClient,
            ed_email: '',
            isIAmEDChecked: false,
            isAgreementPolicyChecked: false,
            isWarRoomChecked: false
          })
          return
        }
        const detailBundle = getDetailsForChargeCode()
        if (!datasetId) {
          finishSuccessfulChargeCode(data, detailBundle)
          return
        }
        return ReportingEndpoints.checkDownloadConsumption(datasetId, chargeCode.value)
          .then(() => {
            finishSuccessfulChargeCode(data, detailBundle)
          })
          .catch((err) => {
            const body = err?.response?.data
            const msg =
              (typeof body === 'string' && body) ||
              body?.message ||
              body?.title ||
              body?.detail ||
              'This charge code cannot be used for the selected dataset.'
            createWarningToast(typeof msg === 'string' ? msg : String(msg))
          })
      })
      .catch(() => {
        createWarningToast('Error validating charge code')
      })
      .finally(() => setLoading(false))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isButtonDisabled) validateAndSubmit()
  }

  const handleInputChange = (e) => {
    setChargeCode({
      value: e.target.value,
      isActive: null,
      isChargeable: null,
      isClient: null,
      ed_email: '',
      isIAmEDChecked: false,
      isAgreementPolicyChecked: false,
      isWarRoomChecked: false
    })
  }

  return (
    <div style={{ width: '550px', fontFamily: "'McKinsey Sans', sans-serif" }}>
      <S.ChargeCodeBodyContainer>
        <S.InputRowsGrid>
          <S.ChargeCodeInputTitle $blue>Charge code</S.ChargeCodeInputTitle>
          <S.ChargeCodeInputContainer>
            <S.ChargeCodeInput
              type="text"
              placeholder="Enter charge code here..."
              value={chargeCode.value || ''}
              $isValid={chargeCode.isActive && chargeCode.isChargeable && chargeCode.isClient}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            {loading && (
              <S.LoaderIconContainer>
                <S.Loader />
              </S.LoaderIconContainer>
            )}
          </S.ChargeCodeInputContainer>
          <span />
          <S.ChargeCodeInputTitle $blue>ED Email</S.ChargeCodeInputTitle>
          <S.ChargeCodeInputContainer>
            <S.ChargeCodeInput
              type="email"
              placeholder="Enter ED email..."
              value={edEmail}
              disabled={isEd}
              onChange={(e) => setEdEmail(e.target.value)}
              $isValid
              style={isEd ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
            />
          </S.ChargeCodeInputContainer>
          <S.EdEmailCheckboxLabel>
            <input
              type="checkbox"
              checked={isEd}
              onChange={(e) => setIsEd(e.target.checked)}
              aria-label="I am ED"
            />
            I am ED
          </S.EdEmailCheckboxLabel>
        </S.InputRowsGrid>
        <ValidationErrorMessage validationKey="inactive" chargeCode={chargeCode} />
        <ValidationErrorMessage validationKey="notChargeable" chargeCode={chargeCode} />
        <ValidationErrorMessage validationKey="nonClient" chargeCode={chargeCode} />
        <ValidationErrorMessage validationKey="tooShort" chargeCode={chargeCode} isEnabled={isEnabled} />
        <S.AgreementCheckboxRow>
          <input
            type="checkbox"
            checked={agreePricingPolicy}
            onChange={(e) => setAgreePricingPolicy(e.target.checked)}
            aria-label="I agree to be charged for this report as per the Pricing Policy"
            aria-required="true"
          />
          <span>
          I agree to be charged for this report as per the Pricing Policy you can find in this window below. I also agree to use this report only for work related to the charge code provided, and to only share access to it with colleagues charging time to this charge code.
          </span>
        </S.AgreementCheckboxRow>
        <S.AgreementCheckboxRow>
          <input
            type="checkbox"
            checked={warRoomChargeCode}
            onChange={(e) => setWarRoomChargeCode(e.target.checked)}
            aria-label="This is a war room / client development hub charge code"
          />
          <span>
            This is a war room / client development hub charge code.
          </span>
        </S.AgreementCheckboxRow>
      </S.ChargeCodeBodyContainer>
      <S.StyledHr />
      <S.InfoLinksContainer>
        <S.InfoLinkRow
          onClick={() => {
            setDisclaimerExpanded(false)
            setPricingExpanded((prev) => !prev)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setDisclaimerExpanded(false)
              setPricingExpanded((prev) => !prev)
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={pricingExpanded}
        >
          <S.InfoIconWrapper>
            <Icon fill="#2251FF" type={TYPE_OUTLINE} src={Outline24CInfo} />
          </S.InfoIconWrapper>
          <S.InfoLinkLabel>View Pricing Information</S.InfoLinkLabel>
        </S.InfoLinkRow>
        {pricingExpanded && (
          <S.ChargeCodeContent>
            <S.PricingSubHeading>Pricing Information</S.PricingSubHeading>
            <S.PricingSubHeading>
              Bundled pricing rates (not applicable to pulls with reduced scope)
            </S.PricingSubHeading>
            <S.PricingList>
              <S.PricingListItem>
                <S.PricingItemLabel>Global market data pulls:</S.PricingItemLabel>{' '}
                $150,000 for AUM, Net Flows and Net Revenues across all selection criteria (all asset classes, client segments, vehicle types) for all markets
              </S.PricingListItem>
              <S.PricingListItem>
                <S.PricingItemLabel>Regional market data pulls:</S.PricingItemLabel>{' '}
                $75,000 for AUM, Net Flows and Net Revenues across all selection criteria (all asset classes, client segments, vehicle types) for all countries within EMEA and APAC
              </S.PricingListItem>
              <S.PricingListItem>
                <S.PricingItemLabel>Single market data pulls:</S.PricingItemLabel>{' '}
                $27,000 for AUM, Net Flows and Net Revenues across all selection criteria (all asset classes, client segments, vehicle types) for a single country; $35,000 for the United States
              </S.PricingListItem>
              <S.PricingListItem>
                <S.PricingItemLabel>For all customized data pulls:</S.PricingItemLabel>{' '}
                $250 per data point (i.e. when selecting one parameter within each of the selection category - metrics, geographies, asset classes, client segments, vehicle types), irrespective of the number of years
              </S.PricingListItem>
            </S.PricingList>
            <S.ContactParagraph>
              In case of any questions related to scope of coverage or chargebacks, please contact{' '}
              <S.ContactLink href="mailto:performancelens_growthcube@mckinsey.com">
                performancelens_growthcube@mckinsey.com
              </S.ContactLink>
            </S.ContactParagraph>
          </S.ChargeCodeContent>
        )}
        <S.InfoLinkRow
          onClick={() => {
            setPricingExpanded(false)
            setDisclaimerExpanded((prev) => !prev)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPricingExpanded(false)
              setDisclaimerExpanded((prev) => !prev)
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={disclaimerExpanded}
        >
          <S.InfoIconWrapper>
            <Icon fill="#2251FF" type={TYPE_OUTLINE} src={Outline24CInfo} />
          </S.InfoIconWrapper>
          <S.InfoLinkLabel>View Disclaimer</S.InfoLinkLabel>
        </S.InfoLinkRow>
        {disclaimerExpanded && (
          <S.ChargeCodeContent>
            <S.DisclaimerParagraph>
              The Global Growth Cube is part of McKinsey's Performance Lens solution. It is a comprehensive asset management market-sizing database, providing granular historical and forward-looking views of asset & revenue pools and net flows by geography, client segment and product to help CSTs and clients make key strategic decisions and resource allocations.
            </S.DisclaimerParagraph>
            <S.DisclaimerParagraph>Please note the following:</S.DisclaimerParagraph>
            <S.PricingList>
              <S.PricingListItem>
                Global Growth Cube data is for use on confirmed client studies and should not be used for other purposes (LoPs, KIPs, whitepapers etc.). Please contact our team at{' '}
                <S.ContactLink href="mailto:performancelens_growthcube@mckinsey.com">
                  performancelens_growthcube@mckinsey.com
                </S.ContactLink>
                {' '}for any data pulls related to work outside of confirmed client studies
              </S.PricingListItem>
              <S.PricingListItem>
                Please refer to the definitions to understand the scope of the data. Definitions can be found in the Documents & Publications section under Help & Support
              </S.PricingListItem>
              <S.PricingListItem>
                The database captures full granularity by geography, client segment and asset class for 29 countries that contribute to over 98% of the global AUM, for the rest of the countries, higher level AUM and flows data is captured
              </S.PricingListItem>
              <S.PricingListItem>
                Asset class level detail is only available for All Vehicles (Total), Total Mutual Funds – EX ETF/ETPs and ETFs/ETPs
              </S.PricingListItem>
              <S.PricingListItem>
                Forecasts are made available for Total Vehicles and ETFs only
              </S.PricingListItem>
              <S.PricingListItem>
                Latest year-end FX rates are applied to all historical years in order to smooth out the impact of FX rates on growth.
              </S.PricingListItem>
              <S.PricingListItem>
                We strive to provide the best data, however, reporting granularity varies across geographies in which case we make estimates based on expert inputs.
              </S.PricingListItem>
              <S.PricingListItem>
                These data are for internal use within the client organization only, and cannot be disclosed in whole or in part outside of your organization without McKinsey & Company's prior written consent.
              </S.PricingListItem>
            </S.PricingList>
            <S.DisclaimerParagraph $bold>
              THESE DATA ARE PROVIDED AS IS, WITHOUT ANY EXPRESS OR IMPLIED WARRANTY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
            </S.DisclaimerParagraph>
            <S.DisclaimerParagraph>
              McKinsey & Company does not warrant any results obtained or conclusions drawn from the use of the data; has no obligation to maintain, update, or correct the data; and shall not be liable to you for any damages or losses resulting from use of the data, including but not limited to lost profits or consequential damages.
            </S.DisclaimerParagraph>
          </S.ChargeCodeContent>
        )}
      </S.InfoLinksContainer>
      <S.ChargeCodeBodyContainer>
        <Button disabled={isButtonDisabled} onClick={validateAndSubmit}>
          {buttonTitle}
        </Button>
      </S.ChargeCodeBodyContainer>
    </div>
  )
}
